import { LISTED, type Restaurant } from "./restaurants";
import type { ShiftResult } from "./shift";

// Career progress: the best stars and pay at each restaurant, and running
// totals. Saved to localStorage when the page is allowed to (not in some
// sandboxed embeds), so everything here also works without it.

export interface Best {
  stars: number;
  pay: number;
}

export interface Progress {
  best: Record<string, Best>;
  shifts: number;
  earnings: number;
}

export const STORAGE_KEY = "orderup.progress";
/** Where progress was saved under the game's earlier names (Odd Jobs, Dish Pit), newest first; read if there's nothing newer. */
export const OLD_STORAGE_KEYS = ["oddjobs.progress", "dishpit.progress"] as const;

export const EMPTY_PROGRESS: Progress = { best: {}, shifts: 0, earnings: 0 };

/** Stars that count toward unlocking: the best at each restaurant on the list (not the parked ones). */
export function totalStars(p: Progress): number {
  return LISTED.reduce((sum, r) => sum + (p.best[r.id]?.stars ?? 0), 0);
}

export function isUnlocked(r: Restaurant, p: Progress): boolean {
  return totalStars(p) >= r.unlockStars;
}

export interface Recorded {
  progress: Progress;
  newBestStars: boolean;
  newBestPay: boolean;
  /** Restaurants this shift's stars unlocked. */
  unlocked: Restaurant[];
}

export function recordShift(p: Progress, restaurantId: string, result: ShiftResult): Recorded {
  const prev = p.best[restaurantId];
  const best: Best = {
    stars: Math.max(prev?.stars ?? 0, result.stars),
    pay: Math.max(prev?.pay ?? 0, result.pay),
  };
  const progress: Progress = {
    best: { ...p.best, [restaurantId]: best },
    shifts: p.shifts + 1,
    earnings: Math.round((p.earnings + result.pay) * 100) / 100,
  };
  return {
    progress,
    newBestStars: result.stars > (prev?.stars ?? 0),
    newBestPay: prev !== undefined && result.pay > prev.pay,
    unlocked: LISTED.filter((r) => !isUnlocked(r, p) && isUnlocked(r, progress)),
  };
}

/** Reads saved progress, dropping anything malformed. */
export function parseProgress(raw: unknown): Progress {
  if (!raw || typeof raw !== "object") return EMPTY_PROGRESS;
  const o = raw as Record<string, unknown>;
  const best: Record<string, Best> = {};
  if (o.best && typeof o.best === "object") {
    for (const [id, b] of Object.entries(o.best as Record<string, unknown>)) {
      const { stars, pay } = (b ?? {}) as Record<string, unknown>;
      if (isCount(stars) && stars <= 3 && isAmount(pay)) best[id] = { stars, pay };
    }
  }
  return {
    best,
    shifts: isCount(o.shifts) ? o.shifts : 0,
    earnings: isAmount(o.earnings) ? o.earnings : 0,
  };
}

export function loadProgress(): Progress {
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    for (const key of OLD_STORAGE_KEYS) raw ??= localStorage.getItem(key);
    return raw ? parseProgress(JSON.parse(raw)) : EMPTY_PROGRESS;
  } catch {
    return EMPTY_PROGRESS;
  }
}

export function saveProgress(p: Progress): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    // Storage blocked (private window, sandboxed embed): progress lasts the session.
  }
}

function isCount(v: unknown): v is number {
  return Number.isInteger(v) && (v as number) >= 0;
}

function isAmount(v: unknown): v is number {
  return typeof v === "number" && Number.isFinite(v) && v >= 0;
}
